// ---------------------------------------------------------------------------
// useAgents — fetch the list of agents for the supplied workspace. When
// `workspaceName` is null the hook stays idle and returns an empty,
// non-loading result so the consumer can render a "pick a workspace"
// placeholder without a network call. Built on the shared `useAsyncData`
// primitive for race-safe loading + refresh.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { AgentsService } from '../../services/agentsService.js';
import type { Agent } from '../../types/agent.js';
import { useAsyncData } from '../useAsyncData/useAsyncData.js';

import type {
  UseAgentsParams,
  UseAgentsResult,
} from './useAgents.interface.js';

export function useAgents(params: UseAgentsParams): UseAgentsResult {
  const { workspaceName } = params;
  const client = useHttpClient();
  const service = useMemo(() => new AgentsService(client), [client]);

  const fetcher = useMemo<
    ((signal: AbortSignal) => Promise<Agent[]>) | null
  >(() => {
    if (workspaceName === null) return null;
    return (signal) => service.list(workspaceName, { signal });
  }, [service, workspaceName]);

  const { data, isLoading, error, refresh } = useAsyncData<Agent[]>(fetcher, {
    errorMessage: 'Failed to load agents',
  });
  const [optimisticAgents, setOptimisticAgents] = useState<Agent[]>([]);

  useEffect(() => {
    setOptimisticAgents([]);
  }, [workspaceName]);

  useEffect(() => {
    if (data === null) return;
    const fetchedNames = new Set(data.map((agent) => agent.name));
    setOptimisticAgents((current) =>
      current.filter((agent) => !fetchedNames.has(agent.name)),
    );
  }, [data]);

  const upsertAgent = useCallback((agent: Agent) => {
    setOptimisticAgents((current) => [
      agent,
      ...current.filter((item) => item.name !== agent.name),
    ]);
  }, []);

  const agents = useMemo(
    () => [
      ...optimisticAgents,
      ...(data ?? []).filter(
        (agent) =>
          !optimisticAgents.some(
            (optimistic) => optimistic.name === agent.name,
          ),
      ),
    ],
    [data, optimisticAgents],
  );

  return useMemo<UseAgentsResult>(
    () => ({ agents, isLoading, error, refresh, upsertAgent }),
    [agents, isLoading, error, refresh, upsertAgent],
  );
}
